<template>
  <v-dialog :value="show" persistent max-width="1000">
    <v-card class="rounded-lg assign-movement-card">
      <v-card-title class="headline px-6 py-4 assign-movement-header">
        <span>Atribuir movimentação</span>
        <v-spacer />
        <v-icon color="foreground-accent" @click="$emit('close')">fas fa-times</v-icon>
      </v-card-title>

      <v-card-text class="pa-0">
        <div
          v-if="hasMultipleStructures"
          class="assign-movement-alert px-6 py-5"
        >
          <v-icon color="primary" class="mr-3">mdi mdi-alert-circle-outline</v-icon>
          <span>
            Você selecionou movimentações de mais de uma estrutura de atendimento.
            Defina o responsável para cada uma.
          </span>
        </div>

        <div
          v-if="assignmentRows.length === 0 && !loadingRows"
          class="assign-movement-alert px-6 py-5"
        >
          <v-icon color="primary" class="mr-3">mdi mdi-information-outline</v-icon>
          <span>Nenhuma movimentação disponível para atribuição.</span>
        </div>

        <div class="pa-6">
          <v-progress-linear
            v-if="loadingRows"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <div
            v-if="assignmentRows.length > 0"
            class="assign-movement-table-scroll"
            :class="{ 'assign-movement-table-scroll--many': assignmentRows.length > 6 }"
          >
            <v-simple-table
              class="assign-movement-table"
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Estrutura de Atendimento</th>
                    <th>Movimentações</th>
                    <th>Responsável</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in assignmentRows" :key="row.key">
                    <td>{{ row.structureName }}</td>
                    <td>{{ row.movementIds.length }}</td>
                    <td class="assign-movement-user-cell">
                      <v-autocomplete
                        v-model="row.selectedUserId"
                        :items="row.users"
                        :loading="row.loadingUsers"
                        :disabled="loadingAssign || row.loadingUsers || row.users.length === 0"
                        item-text="name"
                        item-value="id"
                        placeholder="Usuário responsável"
                        outlined
                        dense
                        color="primary"
                        no-data-text="Nenhum usuário encontrado"
                        hide-details
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-spacer />
        <v-btn
          width="124px"
          color="primary"
          outlined
          @click="$emit('close')"
          :disabled="loadingAssign"
        >
          Cancelar
        </v-btn>
        <v-btn
          width="132px"
          color="primary"
          class="foreground-accent--text"
          @click="confirmAssign"
          :loading="loadingAssign"
          :disabled="!canConfirm"
        >
          Atribuir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MovementAssignmentService from '@/services-http/sdi/MovementAssignmentService';

export default {
  name: 'AssignMovementModal',

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    movementIds: {
      type: Array,
      default: () => [],
    },
    allPossibleSelected: {
      type: Boolean,
      default: false,
    },
    allMovementIds: {
      type: Array,
      default: () => [],
    },
    movementsByStructure: {
      type: Array,
      default: () => [],
    },
    movementItems: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    loadingAssign: false,
    loadingRows: false,
    assignmentRows: [],
  }),

  computed: {
    hasMultipleStructures() {
      return this.assignmentRows.length > 1;
    },
    canConfirm() {
      return this.assignmentRows.length > 0
        && !this.loadingAssign
        && !this.loadingRows
        && this.assignmentRows.every(
          (row) => row.selectedUserId && !row.loadingUsers && row.users.length > 0,
        );
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.resetModal();
        this.prepareAssignmentRows();
      }
    },
  },

  methods: {
    resetModal() {
      this.loadingAssign = false;
      this.loadingRows = false;
      this.assignmentRows = [];
    },
    hasValue(value) {
      return value !== null && typeof value !== 'undefined' && value !== '';
    },
    resolveStructureId(movement) {
      if (!movement) return null;
      return movement.structureId
        || movement.structure_id
        || movement.serviceUnitId
        || movement.service_unit_id
        || movement.serviceUnit
        || null;
    },
    resolveStructureName(movement) {
      if (!movement) return 'Estrutura não informada';
      return movement.structureName
        || movement.structure_name
        || movement.serviceUnitName
        || movement.service_unit_name
        || 'Estrutura não informada';
    },
    normalizeUsers(users = []) {
      const usersById = users.reduce((acc, user) => {
        if (!user || !this.hasValue(user.id)) return acc;
        return {
          ...acc,
          [user.id]: {
            id: user.id,
            name: user.name,
          },
        };
      }, {});

      return Object.values(usersById);
    },
    getFallbackMovementIds() {
      return this.allPossibleSelected ? this.allMovementIds : this.movementIds;
    },
    buildAssignmentRows() {
      if (this.allPossibleSelected && Array.isArray(this.movementsByStructure) && this.movementsByStructure.length > 0) {
        return this.movementsByStructure
          .filter((structure) => structure && Array.isArray(structure.movementIds) && structure.movementIds.length > 0)
          .map((structure) => ({
            key: `structure-${structure.id}`,
            structureId: structure.id,
            structureName: structure.name || 'Estrutura não informada',
            movementIds: [...structure.movementIds],
            users: [],
            selectedUserId: null,
            loadingUsers: false,
          }));
      }

      const movements = Array.isArray(this.movementItems)
        ? this.movementItems.filter((movement) => movement && this.hasValue(movement.id))
        : [];

      if (movements.length === 0) {
        const ids = this.getFallbackMovementIds();
        if (!Array.isArray(ids) || ids.length === 0) return [];

        return [{
          key: 'without-structure',
          structureId: null,
          structureName: 'Estrutura não informada',
          movementIds: ids,
          users: [],
          selectedUserId: null,
          loadingUsers: false,
        }];
      }

      const rowsByStructure = movements.reduce((acc, movement) => {
        const structureId = this.resolveStructureId(movement);
        const structureName = this.resolveStructureName(movement);
        const key = this.hasValue(structureId) ? String(structureId) : structureName;

        if (!acc[key]) {
          acc[key] = {
            key,
            structureId,
            structureName,
            movementIds: [],
            users: [],
            selectedUserId: null,
            loadingUsers: false,
          };
        }

        acc[key].movementIds.push(movement.id);
        return acc;
      }, {});

      return Object.values(rowsByStructure);
    },
    async prepareAssignmentRows() {
      this.assignmentRows = this.buildAssignmentRows();
      if (this.assignmentRows.length === 0) return;

      this.loadingRows = true;
      const assignmentService = new MovementAssignmentService();

      await Promise.all(
        this.assignmentRows.map((row) => this.loadUsersForRow(row, assignmentService)),
      );

      this.loadingRows = false;
    },
    async loadUsersForRow(row, assignmentService) {
      if (!this.hasValue(row.structureId)) return;

      row.loadingUsers = true;

      try {
        const response = await assignmentService.getResponsibleUsers([row.structureId]);
        row.users = this.normalizeUsers(response && response.data ? response.data : []);
      } catch {
        row.users = [];
      } finally {
        row.loadingUsers = false;
      }
    },
    async confirmAssign() {
      if (!this.canConfirm) return;

      this.loadingAssign = true;
      const assignmentService = new MovementAssignmentService();

      try {
        await Promise.all(
          this.assignmentRows.map((row) => assignmentService.assignMovements({
            movementIds: row.movementIds,
            userId: row.selectedUserId,
          })),
        );
        this.$emit('confirm', { success: true });
      } catch {
        this.$emit('confirm', { success: false });
      } finally {
        this.loadingAssign = false;
      }
    },
  },
};
</script>

<style scoped>
.assign-movement-header {
  background-color: var(--v-primary-base);
  color: var(--v-foreground-accent-base);
  font-weight: 700;
}

.assign-movement-alert {
  align-items: center;
  border-bottom: 1px solid var(--v-border-base);
  color: var(--v-base-color-base);
  display: flex;
  font-size: 20px;
  line-height: 1.3;
}

.assign-movement-table {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
}

.assign-movement-table-scroll--many {
  max-height: 400px;
  overflow-y: auto;
}

.assign-movement-table-scroll--many th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.assign-movement-table th {
  background-color: var(--v-muted-foreground-base);
  color: var(--v-base-color-base);
  font-weight: 700;
  white-space: nowrap;
}

.assign-movement-table td {
  color: var(--v-base-color-base);
  height: 60px;
}

.assign-movement-user-cell {
  min-width: 360px;
}
</style>
